import {
  Button,
  Drawer,
  Row,
  Space,
  Form,
  Select,
  Input,
  notification,
} from 'antd';
import {
  useAddTransportMutation,
  useGetAllGpsTransportQuery,
  useGetVehicleAccountingMethodsQuery,
} from 'app/api';
import NumberInput from 'app/components/NumberInput/NumberInput';
import TaskCategorySelect from 'app/components/Select/TaskCategorySelect/TaskCategorySelect';
import TransportTypeSelect from 'app/components/Select/TransportTypeSelect/TransportTypeSelect';
import { selectCompanies } from 'app/containers/Company/slice/selectors';
import { useGlobalSlice } from 'app/containers/Global/slice';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IOption } from 'types';
import { getInteger } from 'utils/helpers';
import { useUpdateTransportMutation } from '../../../../api';
import { getEditTransportFormInitialValues } from '../../helpers';
import {
  ACCOUNTING_METHODS_MAP,
  IAddTransportPayload,
  ITransport,
  ITransportFormValues,
} from '../../types';

interface IProps {
  isOpen: boolean;
  transport?: ITransport;

  onClose: () => void;
}
const TransportDrawer: FC<IProps> = ({ isOpen, onClose, transport }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [form] = Form.useForm();
  const envId = useSelector(selectSelectedEnvId) as number;

  // Company Options
  const allCompanies = useSelector(selectCompanies);
  const companyOptions: IOption[] | undefined = allCompanies?.map(company => ({
    value: company.id,
    label: company.name,
  }));

  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >();

  // GPS Transport Options
  const {
    isFetching: isGpsTransportLoading,
    error: gpsTransportError,
    currentData: allGpsTransport,
  } = useGetAllGpsTransportQuery({ envId }, { skip: !envId });

  const gpsTransportOptions: IOption[] | undefined = allGpsTransport?.map(
    gpsTransport => ({
      value: gpsTransport.id,
      label: `${gpsTransport.licensePlate} (${gpsTransport.gpsId})`,
      companyId: gpsTransport.companyId,
      disabled:
        transport?.id !== gpsTransport.garageVehicleId && gpsTransport.assigned,
    }),
  );

  // Reset gps transport if error on loading options
  useEffect(() => {
    if (gpsTransportError) {
      form.setFieldsValue({ gpsVehicleId: undefined });
      handleError(gpsTransportError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsTransportError]);

  // Reset gps transport when selected company changed
  useEffect(() => {
    const selectedGpsTransportId = form.getFieldValue('gpsVehicleId');

    if (!gpsTransportOptions || !selectedCompanyId || !selectedGpsTransportId) {
      return;
    }

    const isSelectedGpsTransportInOptions = gpsTransportOptions.find(
      gpsTransportOption => gpsTransportOption.value === selectedGpsTransportId,
    );

    if (!isSelectedGpsTransportInOptions) {
      form.setFieldsValue({ gpsVehicleId: undefined });
    }
  }, [selectedCompanyId, isGpsTransportLoading, form, gpsTransportOptions]);

  // Accounting Methods Options
  const {
    data: accountingMethods,
    isFetching: isAccountingMethodsLoading,
  } = useGetVehicleAccountingMethodsQuery();

  const accountingMethodOptions:
    | IOption<number>[]
    | undefined = accountingMethods?.map(method => ({
    value: method.id,
    label: method.name,
  }));

  const [selectedAccountingMethods, setSelectedAccountingMethods] = useState<
    number[]
  >([]);

  // Set edit transport data on open
  useEffect(() => {
    if (isOpen && transport) {
      const values = getEditTransportFormInitialValues(transport);

      setSelectedAccountingMethods(values.accountingMethods);
      setSelectedCompanyId(values.companyId);

      form.setFieldsValue(values);
    }
  }, [transport, isOpen, form]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setSelectedCompanyId(undefined);
      setSelectedAccountingMethods([]);
    }
  }, [isOpen, form]);

  const [
    addTransport,
    { isLoading: isAddSubmitLoading },
  ] = useAddTransportMutation();

  const [
    uppdateTransport,
    { isLoading: isUpdateSubmitLoading },
  ] = useUpdateTransportMutation();

  const isSubmitLoading = isAddSubmitLoading || isUpdateSubmitLoading;

  const onFinish = async (values: ITransportFormValues) => {
    try {
      if (!envId || isSubmitLoading) {
        return;
      }
      const {
        kmCostPrice,
        kmSellPrice,
        haSellPrice,
        haCostPrice,
        motoHourCostPrice,
        motoHourSellPrice,
        ...formValues
      } = values;

      const methods: {
        costPrice: number | undefined;
        id: number;
        sellPrice: number | undefined;
      }[] =
        values.accountingMethods?.map(methodId => {
          if (methodId === ACCOUNTING_METHODS_MAP.ha) {
            return {
              costPrice: getInteger(values.haCostPrice),
              sellPrice: getInteger(values.haSellPrice),
              id: methodId,
            };
          } else if (methodId === ACCOUNTING_METHODS_MAP.km) {
            return {
              costPrice: getInteger(values.kmCostPrice),
              sellPrice: getInteger(values.kmSellPrice),
              id: methodId,
            };
          }
          return {
            costPrice: getInteger(values.motoHourCostPrice),
            sellPrice: getInteger(values.motoHourSellPrice),
            id: methodId,
          };
        }) || [];

      const payload: IAddTransportPayload = {
        ...formValues,
        envId,
        year: values.year ? Number(values.year) : undefined,
        motoHours: values.motoHours ? Number(values.motoHours) : undefined,
        accountingMethods: methods,
      };
      let successMsg = '';

      if (transport) {
        await uppdateTransport({
          ...payload,
          transportId: transport.id,
        }).unwrap();
        successMsg = t(
          'Transport.Drawer.TransportSuccessfullyUpdated',
          'Transport successfully updated',
        );
      } else {
        await addTransport(payload).unwrap();
        successMsg = t(
          'Transport.Drawer.TransportSuccessfullyAdded',
          'Transport successfully added',
        );
      }

      onClose();
      notification.success({
        message: successMsg,
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  const title = transport
    ? t('Transport.Drawer.EditTitle', 'Edit transport')
    : t('Transport.Drawer.Title', 'Add transport');

  return (
    <Drawer
      title={title}
      visible={isOpen}
      forceRender={true}
      onClose={onClose}
      destroyOnClose
      placement="right"
      width={400}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose}>{t('Common.Cancel', 'Cancel')}</Button>

            <Button
              loading={isSubmitLoading}
              form="addTransportForm"
              key="submit"
              htmlType="submit"
              type="primary"
            >
              {t('Common.Save', 'Save')}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical' }}
        requiredMark={true}
        id="addTransportForm"
        onFinish={onFinish}
      >
        <Form.Item
          label={t('Transport.Type', 'Type')}
          name="typeId"
          rules={[{ required: true }]}
        >
          <TransportTypeSelect
            bordered={true}
            dropdownMatchSelectWidth={false}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          label={t('Transport.Model', 'Model')}
          name="model"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Transport.Model', 'Model')} />
        </Form.Item>

        <Form.Item
          label={t('Transport.LicensePlate', 'License plate')}
          name="licensePlate"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Transport.LicensePlate', 'License plate')} />
        </Form.Item>

        <Form.Item
          label={t('Transport.Ownership', 'Ownership')}
          name="companyId"
          rules={[{ required: true }]}
        >
          <Select
            bordered={true}
            placeholder={t('Transport.Ownership', 'Ownership')}
            dropdownMatchSelectWidth={false}
            options={companyOptions}
            value={selectedCompanyId}
            onChange={setSelectedCompanyId}
            allowClear
          />
        </Form.Item>

        <Form.Item
          label={t('Transport.GpsId', 'GPS ID')}
          name="gpsVehicleId"
          rules={[{ required: false }]}
        >
          <Select
            onChange={(value, option) => {
              if (option && 'companyId' in option && !selectedCompanyId) {
                setSelectedCompanyId(option.companyId);
                form.setFieldsValue({ companyId: option.companyId });
              }
            }}
            bordered={true}
            placeholder={t('Transport.GpsId', 'GPS ID')}
            dropdownMatchSelectWidth={false}
            options={gpsTransportOptions}
            loading={isGpsTransportLoading}
            allowClear
          />
        </Form.Item>

        <Form.Item
          label={t('Transport.Width', 'Operating width (m)')}
          name="width"
        >
          <NumberInput
            placeholder={t('Transport.Width', 'Operating width (m)')}
          />
        </Form.Item>

        <Space size={36}>
          <Form.Item
            label={t('Transport.Year', 'Year of production')}
            name="year"
          >
            <NumberInput
              placeholder={t('Transport.Year', 'Year of production')}
            />
          </Form.Item>

          <Form.Item
            label={t('Transport.MotoHours', 'Motohours')}
            name="motoHours"
          >
            <NumberInput placeholder={t('Transport.MotoHours', 'Motohours')} />
          </Form.Item>
        </Space>

        <Form.Item
          label={t(
            'Transport.MainCategoryWithouImplement',
            'Main category of tasks to be performed without the implement',
          )}
          name="defaultTaskCategoryId"
          rules={[{ required: false }]}
        >
          <TaskCategorySelect
            bordered={true}
            placeholder={t(
              'Transport.MainCategoryWithouImplement',
              'Main category of tasks to be performed without the implement',
            )}
            dropdownMatchSelectWidth={false}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          label={t(
            'Transport.CategoriesWithoutImplement',
            'Categories of tasks to be performed without the implement',
          )}
          name="taskCategoryIds"
        >
          <TaskCategorySelect
            mode="multiple"
            bordered={true}
            placeholder={t(
              'Transport.CategoriesWithoutImplement',
              'Categories of tasks to be performed without the implement',
            )}
            dropdownMatchSelectWidth={false}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          label={t('Transport.CalculationMethod', 'Calculation method')}
          name="accountingMethods"
        >
          <Select
            mode="multiple"
            bordered={true}
            placeholder={t('Transport.CalculationMethod', 'Calculation method')}
            dropdownMatchSelectWidth={false}
            options={accountingMethodOptions}
            value={selectedAccountingMethods}
            onChange={value => setSelectedAccountingMethods(value)}
            allowClear={true}
            loading={isAccountingMethodsLoading}
          />
        </Form.Item>

        {selectedAccountingMethods.includes(
          ACCOUNTING_METHODS_MAP.motoHours,
        ) && (
          <>
            <Form.Item
              label={t('Transport.MotoHourCostPrice', 'Motohour cost price')}
              name="motoHourCostPrice"
            >
              <NumberInput
                placeholder={t(
                  'Transport.MotoHourCostPrice',
                  'Motohour cost price',
                )}
              />
            </Form.Item>

            <Form.Item
              label={t('Transport.MotoHourSellPrice', 'Motohour sell price')}
              name="motoHourSellPrice"
            >
              <NumberInput
                placeholder={t(
                  'Transport.MotoHourSellPrice',
                  'Motohour sell price',
                )}
              />
            </Form.Item>
          </>
        )}

        {selectedAccountingMethods.includes(ACCOUNTING_METHODS_MAP.ha) && (
          <>
            <Form.Item
              label={t('Transport.HaCostPrice', 'Ha cost price')}
              name="haCostPrice"
            >
              <NumberInput
                placeholder={t('Transport.HaCostPrice', 'Ha cost price')}
              />
            </Form.Item>

            <Form.Item
              label={t('Transport.HaSellPrice', 'Ha sell price')}
              name="haSellPrice"
            >
              <NumberInput
                placeholder={t('Transport.HaSellPrice', 'Ha sell price')}
              />
            </Form.Item>
          </>
        )}

        {selectedAccountingMethods.includes(ACCOUNTING_METHODS_MAP.km) && (
          <>
            <Form.Item
              label={t('Transport.KmCostPrice', 'Km cost price')}
              name="kmCostPrice"
            >
              <NumberInput
                placeholder={t('Transport.KmCostPrice', 'Km cost price')}
              />
            </Form.Item>

            <Form.Item
              label={t('Transport.KmSellPrice', 'Km sell price')}
              name="kmSellPrice"
            >
              <NumberInput
                placeholder={t('Transport.KmSellPrice', 'Km sell price')}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default TransportDrawer;
